import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import AxiosApiCall from "../../api/AxiosApiCall";
import API_URLS from "../../api/ApiServices";
import { toast } from "react-toastify";

const axiosInstance = AxiosApiCall();

const REPORT_TYPES = [
  { value: "vendor_history", label: "Vendor History", description: "Chronological purchases and payments per vendor" },
  { value: "bank_reconciliation", label: "Bank Reconciliation", description: "Bank account credits and debits" },
  { value: "purchase_log", label: "Purchase Log", description: "All admin purchase orders" },
  { value: "allocation_log", label: "Allocation Log", description: "Stock allocations to head offices" },
];

const ExportReports = () => {
  const [reportType, setReportType] = useState("vendor_history");
  const [format, setFormat] = useState("csv");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ report_type: reportType, format });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await axiosInstance.get(`${API_URLS.exportInventoryReport}?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${reportType}_${new Date().toISOString().slice(0, 10)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded successfully!");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to export report");
    } finally {
      setLoading(false);
    }
  };

  const selectedReport = REPORT_TYPES.find((r) => r.value === reportType);

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Export Reports</h1>
          <p className="text-gray-500 text-sm mt-1">Download inventory and financial reports in CSV format</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Report selector */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-4">Select Report Type</h2>
            <div className="space-y-3">
              {REPORT_TYPES.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${
                    reportType === r.value ? "border-orange1 bg-orange-50" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="report_type"
                    value={r.value}
                    checked={reportType === r.value}
                    onChange={(e) => setReportType(e.target.value)}
                    className="mt-0.5 accent-orange-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{r.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-4">Export Options</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-4 py-2 border border-orange1 bg-orange-50 text-orange-700 rounded-lg cursor-pointer text-sm font-medium">
                  <input
                    type="radio"
                    name="format"
                    value="csv"
                    checked={true}
                    disabled
                    className="accent-orange-500"
                  />
                  CSV
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range (Optional)</label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Selected Report</p>
              <p className="text-sm font-semibold text-gray-800">{selectedReport?.label}: {format.toUpperCase()}</p>
              {(startDate || endDate) && (
                <p className="text-xs text-gray-500 mt-1">{startDate || "—"} to {endDate || "—"}</p>
              )}
            </div>

            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full py-3 bg-orange1 text-white rounded-lg hover:bg-orange-600 transition font-medium disabled:opacity-50"
            >
              {loading ? "Generating Report..." : `Download ${format.toUpperCase()} Report`}
            </button>
          </div>
        </div>
      </div>
    </MainLayouts>
  );
};

export default ExportReports;
